import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { COURSE_API_URL } from './lib/api-config';
import AlertMessage from './components/ui/AlertMessage';

const api = axios.create({ withCredentials: true });

const STATUS_LABELS = {
  pending: 'Ny',
  contacted: 'Kontaktad',
  booked: 'Bokad',
  declined: 'Avböjt'
};

const STATUS_COLORS = {
  pending: '#ff9800',
  contacted: '#2196f3',
  booked: '#4caf50',
  declined: '#9e9e9e'
};

function CityInterests() {
  const [interests, setInterests] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [view, setView] = useState('summary'); // 'summary' or 'list'

  // Filters
  const [filterCourseId, setFilterCourseId] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Unique courses and cities for filter dropdowns
  const [courses, setCourses] = useState([]);
  const [cities, setCities] = useState([]);

  // Fetch data on mount
  useEffect(() => {
    fetchSummary();
    fetchInterests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when filters change
  useEffect(() => {
    fetchInterests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCourseId, filterCity, filterStatus]);

  const fetchSummary = async () => {
    try {
      const response = await api.get(`${COURSE_API_URL}/interest/summary`);
      setSummary(response.data.data || []);

      // Extract unique courses
      const uniqueCourses = [];
      const seenIds = new Set();
      (response.data.data || []).forEach(item => {
        if (item.course && !seenIds.has(item.course.id)) {
          seenIds.add(item.course.id);
          uniqueCourses.push(item.course);
        }
      });
      setCourses(uniqueCourses);

      // Extract unique cities
      const uniqueCities = [...new Set((response.data.data || []).map(item =>
        item.city === 'Valfri' ? item.customCity : item.city
      ))].filter(Boolean).sort();
      setCities(uniqueCities);
    } catch (err) {
      console.error('Error fetching summary:', err);
      setError('Kunde inte hämta sammanfattning');
    }
  };

  const fetchInterests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterCourseId) params.append('courseId', filterCourseId);
      if (filterCity) params.append('city', filterCity);
      if (filterStatus) params.append('status', filterStatus);

      const url = `${COURSE_API_URL}/interest${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      setInterests(response.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching interests:', err);
      setError('Kunde inte hämta intresseanmälningar');
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`${COURSE_API_URL}/interest/${id}`, { status: newStatus });
      setSuccess('Status uppdaterad');
      setTimeout(() => setSuccess(''), 3000);
      fetchInterests();
      fetchSummary();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Kunde inte uppdatera status');
      setTimeout(() => setError(''), 3000);
    }
  };

  const deleteInterest = async (id) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna intresseanmälan?')) return;

    try {
      await api.delete(`${COURSE_API_URL}/interest/${id}`);
      setSuccess('Intresseanmälan borttagen');
      setTimeout(() => setSuccess(''), 3000);
      fetchInterests();
      fetchSummary();
    } catch (err) {
      console.error('Error deleting interest:', err);
      setError('Kunde inte ta bort intresseanmälan');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getEffectiveCity = (item) => {
    return item.city === 'Valfri' ? item.customCity : item.city;
  };

  const filterSummaryByCity = (city) => {
    setFilterCity(city);
    setView('list');
  };

  const filterSummaryByCourse = (courseId) => {
    setFilterCourseId(courseId);
    setView('list');
  };

  return (
    <main className="main-content">
      <div className="page-header">
        <h2>Intresseanmälningar per ort</h2>
        <p className="page-description">
          Hantera intresseanmälningar för kurser i specifika orter. När 4+ personer är intresserade kan ni planera ett kurstillfälle.
        </p>
      </div>

      <AlertMessage error={error} success={success} />

      <div className="tabs" style={{ marginBottom: '20px' }}>
        <button
          className={`tab-btn ${view === 'summary' ? 'active' : ''}`}
          onClick={() => setView('summary')}
        >
          Sammanfattning
        </button>
        <button
          className={`tab-btn ${view === 'list' ? 'active' : ''}`}
          onClick={() => setView('list')}
        >
          Alla anmälningar ({interests.length})
        </button>
      </div>

      {view === 'summary' ? (
        <div className="card">
          <h3>Intresse per kurs och ort</h3>
          {summary.length === 0 ? (
            <p style={{ color: '#666', padding: '20px 0' }}>Inga intresseanmälningar ännu.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kurs</th>
                  <th>Ort</th>
                  <th>Antal</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {summary.map((item, idx) => {
                  const count = parseInt(item.count) || 0;
                  const pendingCount = parseInt(item.pendingCount) || 0;
                  const effectiveCity = getEffectiveCity(item);
                  const isReady = count >= 4;

                  return (
                    <tr key={idx}>
                      <td>
                        <a
                          href="#filter"
                          onClick={(e) => { e.preventDefault(); filterSummaryByCourse(item.courseId); }}
                          style={{ color: '#2ea3f2', textDecoration: 'none', fontWeight: 600 }}
                        >
                          {item.course?.name || 'Okänd kurs'}
                        </a>
                      </td>
                      <td>
                        <a
                          href="#filter"
                          onClick={(e) => { e.preventDefault(); filterSummaryByCity(item.city); }}
                          style={{ color: '#333', textDecoration: 'none' }}
                        >
                          {effectiveCity}
                        </a>
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            backgroundColor: isReady ? '#e8f5e9' : '#fff3e0',
                            color: isReady ? '#2e7d32' : '#f57c00',
                            fontWeight: 600
                          }}
                        >
                          {count} {isReady ? '(Redo!)' : ''}
                        </span>
                      </td>
                      <td>
                        <span style={{ color: '#666', fontSize: '0.9em' }}>
                          {pendingCount} nya
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-small btn-primary"
                          onClick={() => {
                            setFilterCourseId(item.courseId);
                            setFilterCity(item.city);
                            setView('list');
                          }}
                        >
                          Visa
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="filters" style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>Kurs</label>
              <select
                value={filterCourseId}
                onChange={(e) => setFilterCourseId(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '200px' }}
              >
                <option value="">Alla kurser</option>
                {courses.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>Ort</label>
              <select
                value={filterCity}
                onChange={(e) => setFilterCity(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '150px' }}
              >
                <option value="">Alla orter</option>
                {cities.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '150px' }}
              >
                <option value="">Alla</option>
                <option value="pending">Nya</option>
                <option value="contacted">Kontaktade</option>
                <option value="booked">Bokade</option>
                <option value="declined">Avböjt</option>
              </select>
            </div>
            {(filterCourseId || filterCity || filterStatus) && (
              <div style={{ alignSelf: 'flex-end' }}>
                <button
                  className="btn btn-small"
                  onClick={() => {
                    setFilterCourseId('');
                    setFilterCity('');
                    setFilterStatus('');
                  }}
                  style={{ padding: '8px 16px' }}
                >
                  Rensa filter
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <p>Laddar...</p>
          ) : interests.length === 0 ? (
            <p style={{ color: '#666', padding: '20px 0' }}>
              Inga intresseanmälningar matchar filtret.
            </p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Namn</th>
                  <th>E-post</th>
                  <th>Telefon</th>
                  <th>Företag</th>
                  <th>Kurs</th>
                  <th>Ort</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {interests.map(interest => (
                  <tr key={interest.id}>
                    <td>{formatDate(interest.createdAt)}</td>
                    <td style={{ fontWeight: 500 }}>
                      {interest.firstName} {interest.lastName}
                    </td>
                    <td>
                      <a href={`mailto:${interest.email}`} style={{ color: '#2ea3f2' }}>
                        {interest.email}
                      </a>
                    </td>
                    <td>
                      {interest.phone ? (
                        <a href={`tel:${interest.phone}`} style={{ color: '#333' }}>
                          {interest.phone}
                        </a>
                      ) : '-'}
                    </td>
                    <td>{interest.company || '-'}</td>
                    <td>{interest.course?.name || '-'}</td>
                    <td>{getEffectiveCity(interest)}</td>
                    <td>
                      <select
                        value={interest.status}
                        onChange={(e) => updateStatus(interest.id, e.target.value)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          backgroundColor: STATUS_COLORS[interest.status] + '20',
                          color: STATUS_COLORS[interest.status],
                          fontWeight: 600
                        }}
                      >
                        {Object.entries(STATUS_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => deleteInterest(interest.id)}
                        title="Ta bort"
                      >
                        Ta bort
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <style>{`
        .tabs {
          display: flex;
          gap: 10px;
        }
        .tab-btn {
          padding: 10px 20px;
          border: 2px solid #2ea3f2;
          background: white;
          color: #2ea3f2;
          border-radius: 5px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }
        .tab-btn:hover {
          background: #f0f7ff;
        }
        .tab-btn.active {
          background: #2ea3f2;
          color: white;
        }
        .alert {
          padding: 12px 15px;
          border-radius: 5px;
          margin-bottom: 20px;
        }
        .alert-error {
          background: #ffebee;
          color: #c62828;
          border: 1px solid #ef9a9a;
        }
        .alert-success {
          background: #e8f5e9;
          color: #2e7d32;
          border: 1px solid #a5d6a7;
        }
        .btn-danger {
          background: transparent;
          color: #e53935;
          border: 1px solid #e53935;
        }
        .btn-danger:hover {
          background: #e53935;
          color: white;
        }
      `}</style>
    </main>
  );
}

export default CityInterests;
